import FlightSearchWidgetDispatchContext from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetDispatchContext'
import FlightSearchWidgetStateContext from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetStateContext'
import { FlightSearchWidgetActions } from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetStateReducer'
import React, { useCallback, useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'
import FlightSearchAirportModalSelect from './FlightSearchAirportModalSelect'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import Modal from 'LuxKit/Modal/Modal'

interface Props {
  direction: 'departure' | 'arrival'
  id: string
}

function FlightSearchAirportModal(props: Props) {
  const { direction, id } = props
  const { resolve } = useModalElementContext<App.AirportLocation | undefined>()
  const intl = useIntl()

  const state = useContext(FlightSearchWidgetStateContext)
  const dispatch = useContext(FlightSearchWidgetDispatchContext)

  const { flights } = state

  const flight = useMemo(() => {
    return flights.find((flight) => flight.id === id)
  }, [flights, id])!

  const prevAirport = useMemo(() => {
    const currentFlightIndex = flights.findIndex(flight => flight.id === id)
    return flights[currentFlightIndex - 1]?.arrivalAirport
  }, [flights, id])

  const isDeparture = direction === 'departure'
  const airport = isDeparture ? flight?.departureAirport : flight?.arrivalAirport

  const modalTitle = useMemo(
    () =>
      isDeparture
        ? intl.formatMessage({
            id: 'fi_qyLnJQn98woWEBfW3',
            defaultMessage: 'Flying from?',
            description: 'Modal title for selecting departure airport dialog window',
          })
        : intl.formatMessage({
            id: 't092CyJ9Rk_8WuKePgAQ',
            defaultMessage: 'Flying to?',
            description: 'Modal title for selecting arrival airport dialog window',
          }),
    [intl, isDeparture],
  )

  const fallbackHeading = useMemo(
    () =>
      prevAirport && isDeparture
        ? intl.formatMessage({
            id: 'NVeBwql4Mx4hrCxF-eXh',
            defaultMessage: 'Recent search',
            description: 'Heading for recent airport search results in the search interface',
          })
        : undefined,
    [intl, prevAirport, isDeparture],
  )

  const onSelectAirport = useCallback(
    (airport?: App.AirportLocation) => {
      const actionType = isDeparture ? FlightSearchWidgetActions.SET_DEPARTURE_AIRPORT : FlightSearchWidgetActions.SET_ARRIVAL_AIRPORT
      dispatch({ type: actionType, airport, id })

      if (airport) {
        resolve(airport)
      }
    },
    [dispatch, isDeparture, resolve, id],
  )

  return (
    <Modal mode="drawer" title={modalTitle}>
      <FlightSearchAirportModalSelect
        airport={airport}
        alternateAirport={isDeparture ? flight?.arrivalAirport : flight?.departureAirport}
        onSelectAirport={onSelectAirport}
        direction={isDeparture ? 'departure' : 'arrival'}
        fallbackAirports={prevAirport?.cityDesc && isDeparture ? [prevAirport] : undefined}
        fallbackHeading={fallbackHeading}
      />
    </Modal>
  )
}

export default FlightSearchAirportModal
